<script setup lang="ts">
import { computed } from 'vue'

import type { InsightConfig } from '../../../models/insightConfig.types'
import type { InsightPreviewState } from '../../../models/workbench.types'
import { outcomePresentation, severityPresentation } from '../../../service/insightPresentation'
import { formatStructuredInsightValue } from '../../../service/insightValue'
import InsightElementList from '../../display/InsightElementList.vue'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { RichTextViewer } from '@/shared/ui/editor'
import { Skeleton } from '@/shared/ui/skeleton'

const props = defineProps<{
  config: InsightConfig | null
  state: InsightPreviewState
}>()

const { t } = useI18n()
const format = useFormat()
const insight = computed(() => (props.state.kind === 'preview_ready' ? props.state.insight : null))
const outcome = computed(() => outcomePresentation(insight.value?.outcome_category))
const severity = computed(() => severityPresentation(insight.value?.severity))
const rowCount = computed(() =>
  (insight.value?.elements ?? []).reduce((total, element) => total + element.row_count, 0),
)

function formatImpact(): string {
  const value = Number(insight.value?.impact_score)
  if (!Number.isFinite(value)) return t('insights.sysadmin.preview.notAvailable')
  if (insight.value?.impact_format === 'currency') {
    return format.formatCurrency(value, { maxFrac: 2 })
  }
  if (insight.value?.impact_format === 'percentage') {
    return format.formatPercent(value, { scale: 'percent', maxFrac: 2 })
  }
  return format.formatNumber(value, { maxFrac: 2, heavyDefault: true })
}

function message(value: string): string {
  return value.startsWith('insights.') ? t(value) : value
}
</script>

<template>
  <section class="preview-panel grid gap-md min-w-0" data-testid="insight-preview-panel">
    <div v-if="state.kind === 'idle'" class="preview-panel__empty p-2xl text-muted text-center">
      <h2>{{ config?.label || config?.enum || t('insights.sysadmin.preview.title') }}</h2>
      <p>{{ t('insights.sysadmin.preview.idle') }}</p>
    </div>

    <div
      v-else-if="state.kind === 'loading'"
      class="preview-panel__loading grid gap-md min-w-0"
      aria-busy="true"
    >
      <Skeleton height="4rem" />
      <Skeleton height="12rem" />
    </div>

    <Alert v-else-if="state.kind === 'error'" tone="danger">
      {{ t('insights.sysadmin.preview.error') }}
    </Alert>

    <Alert v-else-if="state.kind === 'skipped'" tone="warning">
      {{ message(state.message) }}
    </Alert>

    <template v-else>
      <div v-if="insight" class="preview-panel__stack grid gap-md min-w-0">
        <header
          class="preview-panel__hero flex items-start justify-between gap-md max-[44rem]:flex-col"
        >
          <div>
            <p>{{ insight.config_enum || config?.enum }}</p>
            <h2>{{ insight.title || insight.label || t('insights.detail.untitled') }}</h2>
          </div>
          <div class="preview-panel__badges flex flex-wrap gap-xs">
            <Badge :tone="outcome.tone" strong>{{ t(outcome.labelKey) }}</Badge>
            <Badge :tone="severity.tone" strong>{{ t(severity.labelKey) }}</Badge>
          </div>
        </header>

        <div
          class="preview-panel__metrics grid grid-cols-3 gap-sm max-[44rem]:grid-cols-[minmax(0,_1fr)]"
        >
          <div>
            <span>{{ t('insights.sysadmin.preview.impact') }}</span>
            <strong>{{ formatImpact() }}</strong>
          </div>
          <div>
            <span>{{ t('insights.sysadmin.preview.rows') }}</span>
            <strong>{{ format.formatNumber(rowCount) }}</strong>
          </div>
          <div>
            <span>{{ t('insights.sysadmin.preview.elements') }}</span>
            <strong>{{ format.formatNumber(insight.elements.length) }}</strong>
          </div>
        </div>

        <RichTextViewer v-if="insight.content" :content="insight.content" />
        <InsightElementList :elements="insight.elements" :tabs="insight.tabs ?? []" />

        <details v-if="insight.llm_metadata || insight.template_context">
          <summary>{{ t('insights.sysadmin.preview.metadata') }}</summary>
          <pre><code>{{ formatStructuredInsightValue({ llm_metadata: insight.llm_metadata, template_context: insight.template_context }) }}</code></pre>
        </details>
      </div>
      <Alert v-else tone="info">{{ t('insights.sysadmin.preview.readyWithoutInsight') }}</Alert>
    </template>
  </section>
</template>

<style scoped>
.preview-panel .preview-panel__empty h2 {
  margin: 0;
}
.preview-panel .preview-panel__empty p {
  margin: 0;
}
.preview-panel .preview-panel__hero h2 {
  margin: 0;
}
.preview-panel .preview-panel__hero p {
  margin: 0;
}
.preview-panel .preview-panel__metrics div {
  display: grid;
  gap: var(--ui-space-2xs);
  padding: var(--ui-space-md);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-md);
  background-color: var(--ui-color-surface-sunken);
}
.preview-panel .preview-panel__metrics span {
  color: var(--ui-color-muted);
  font-size: 0.75rem;
}
.preview-panel .preview-panel__metrics strong {
  font-size: 1.25rem;
}
.preview-panel details {
  padding: var(--ui-space-md);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-md);
}
.preview-panel pre {
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
